// CapApplied.tsx - Enhanced Version
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Skeleton,
  Divider,
  Stack,
  Tooltip
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import { Application } from "@microsoft/microsoft-graph-types";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PolicyIcon from "@mui/icons-material/Policy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DevicesIcon from "@mui/icons-material/Devices";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import BlockIcon from "@mui/icons-material/Block";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DescriptionIcon from "@mui/icons-material/Description";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShieldIcon from "@mui/icons-material/Shield";

interface CapsAppliedProps {
  app: Application;
}

const CapsApplied: React.FC<CapsAppliedProps> = ({ app }) => {
  const { instance, accounts } = useMsal();

  const [servicePrincipalId, setServicePrincipalId] = useState<string | null>(null);
  const [capLoading, setCapLoading] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);
  const [caps, setCaps] = useState<any[]>([]);
  const spId = servicePrincipalId;

  useEffect(() => {
    if (!app.appId) return;

    const fetchData = async () => {
      try {
        setCapLoading(true);
        setCapError(null);

        // Acquire token for both Application & Conditional Access scopes
        const tokenResp = await instance.acquireTokenSilent({
          scopes: ["Application.Read.All", "Policy.Read.All"],
          account: accounts[0],
        });

        const graphClient = Client.init({
          authProvider: (done) => done(null, tokenResp.accessToken),
        });

        // 1) Get Service Principal
        const spRes = await graphClient
          .api(`/servicePrincipals?$filter=appId eq '${app.appId}'`)
          .get();

        console.log("Service Principal Response:", spRes); // added for debugging purposes

        let spId: string | null = null;
        if (spRes.value && spRes.value.length > 0) {
          spId = spRes.value[0].id;
          setServicePrincipalId(spId);
        } else {
          console.warn("No Service Principal found for appId:", app.appId); // added for debugging purposes
          setServicePrincipalId(null);
        }

        if (!spId) {
          setCaps([]);
          return;
        }

        // 2) Fetch all CAPs
        const polRes = await graphClient.api("/identity/conditionalAccess/policies").get();
        const allPolicies = polRes.value || [];

        // 3) Filter them to see if they apply
        const relevant = allPolicies.filter((policy: any) =>
          isPolicyAppliedToApp(policy, app.appId!) // Compare against appId instead of spId
        );
        
        setCaps(relevant);
      } catch (err: any) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            // Fallback: interactive popup
            const popupResp = await instance.acquireTokenPopup({
              scopes: ["Application.Read.All", "Policy.Read.All"],
              account: accounts[0],
            });
            const graphClient = Client.init({
              authProvider: (done) => done(null, popupResp.accessToken),
            });

            // Repeat steps after popup
            const spRes = await graphClient
              .api(`/servicePrincipals?$filter=appId eq '${app.appId}'`)
              .get();
            let spId: string | null = null;
            if (spRes.value && spRes.value.length > 0) {
              spId = spRes.value[0].id;
              setServicePrincipalId(spId);
            } else {
              setServicePrincipalId(null);
            }
            if (!spId) {
              setCaps([]);
              return;
            }
            // fetch policies
            const polRes = await graphClient.api("/identity/conditionalAccess/policies").get();
            const allPolicies = polRes.value || [];
            const relevant = allPolicies.filter((policy: any) =>
              isPolicyAppliedToApp(policy, spId!)
            );
            setCaps(relevant);
          } catch (popupErr) {
            console.error("Failed in popup:", popupErr);
            setCapError("User canceled or other error acquiring token with popup");
          }
        } else {
          console.error("Error fetching CAP data:", err);
          setCapError("Failed to fetch CAP data");
        }
      } finally {
        setCapLoading(false);
      }
    };

    fetchData();
  }, [app.appId, instance, accounts, servicePrincipalId]);

  // Helper to check if policy applies
  function isPolicyAppliedToApp(policy: any, appId: string): boolean {
    console.log("Evaluating policy:", policy.displayName, policy.conditions);
    
    const appConds = policy?.conditions?.applications || {};
    const includedApps = appConds.includeApplications ?? [];
    const excludedApps = appConds.excludeApplications ?? [];
    const includeAll = appConds.includeAllApplications === true || includedApps.includes("All"); // ✅ Check for "All"
  
    // Debugging output
    console.log("Policy Conditions:", {
      policyName: policy.displayName,
      includedApps,
      excludedApps,
      includeAll
    });
  
    // If policy applies to all cloud apps or all resources, check if app is NOT excluded
    if (includeAll) {
      console.log(`✅ Policy '${policy.displayName}' applies to ALL apps except:`, excludedApps);
      return !excludedApps.includes(appId);
    }
  
    // If app is explicitly included
    if (includedApps.includes(appId)) {
      console.log(`✅ Policy '${policy.displayName}' explicitly includes`, appId);
      return !excludedApps.includes(appId);
    }
  
    console.log(`❌ Policy '${policy.displayName}' does not apply to`, appId);
    return false;
  }

    // Helper function to get policy state styling
    const getPolicyStateChip = (state: string) => {
      if (state === "enabled") {
        return <Chip 
          size="small" 
          color="success" 
          icon={<CheckCircleIcon />} 
          label="Enabled" 
          sx={{ fontWeight: 500 }} 
        />;
      } else if (state === "enabledForReportingButNotEnforced") {
        return <Chip 
          size="small" 
          color="warning" 
          label="Report-only" 
          sx={{ fontWeight: 500 }} 
        />;
      } else {
        return <Chip 
          size="small" 
          color="default" 
          icon={<CancelIcon />} 
          label="Disabled" 
          sx={{ fontWeight: 500 }} 
        />;
      }
    };

    // Helper function to determine policy severity/impact
    const getPolicySeverity = (policy: any) => {
      const grantControls = policy.grantControls?.builtInControls || [];
      
      if (grantControls.includes('block')) {
        return { level: 'critical', label: 'Blocks Access', color: 'error' };
      }
      
      const hasStrictControls = grantControls.some((control: string) => 
        ['mfa', 'compliantDevice', 'domainJoinedDevice'].includes(control)
      );
      
      if (hasStrictControls && policy.grantControls?.operator === 'AND') {
        return { level: 'high', label: 'High Security', color: 'error' };
      }
      
      if (hasStrictControls) {
        return { level: 'medium', label: 'Medium Security', color: 'warning' };
      }
      
      if (policy.state === 'enabledForReportingButNotEnforced') {
        return { level: 'info', label: 'Monitoring Only', color: 'info' };
      }
      
      return { level: 'low', label: 'Low Impact', color: 'default' };
    };

    // Helper function to format grant control labels
    const formatGrantControl = (control: string) => {
      const controlLabels: { [key: string]: string } = {
        'block': 'Block Access',
        'mfa': 'Multi-Factor Authentication',
        'compliantDevice': 'Compliant Device Required',
        'domainJoinedDevice': 'Domain-Joined Device Required',
        'approvedApplication': 'Approved Application Required',
        'compliantApplication': 'Compliant Application Required',
        'passwordChange': 'Password Change Required',
        'unknownFutureValue': 'Additional Control'
      };
      return controlLabels[control] || control;
    };

    // Helper function to get icon for grant control
    const getGrantControlIcon = (control: string) => {
      switch (control) {
        case 'block': return <BlockIcon fontSize="small" />;
        case 'mfa': return <VpnKeyIcon fontSize="small" />;
        case 'compliantDevice': return <PhoneAndroidIcon fontSize="small" />;
        case 'domainJoinedDevice': return <DevicesIcon fontSize="small" />;
        case 'approvedApplication': return <VerifiedUserIcon fontSize="small" />;
        case 'compliantApplication': return <VerifiedUserIcon fontSize="small" />;
        case 'passwordChange': return <LockIcon fontSize="small" />;
        default: return <SecurityIcon fontSize="small" />;
      }
    };
    
    // Helper function to format the conditions for better display
    const getFormattedConditions = (policy: any) => {
      const conditions = policy.conditions || {};
      
      // Format users
      const users = conditions.users || {};
      const userGroups = users.includeGroups || [];
      const excludeGroups = users.excludeGroups || [];
      
      // Format applications (already handled in your code)
      const apps = conditions.applications || {};
      const includeAll = apps.includeAllApplications === true;
      const includeApps = apps.includeApplications || [];
      const excludeApps = apps.excludeApplications || [];
      
      // Format client applications and platforms
      const clientApps = conditions.clientAppTypes || [];
      const platforms = conditions.platforms?.includePlatforms || [];
      const excludePlatforms = conditions.platforms?.excludePlatforms || [];
      
      // Format locations
      const locations = conditions.locations || {};
      const includeLocations = locations.includeLocations || [];
      const excludeLocations = locations.excludeLocations || [];
      
      return {
        users: { includeGroups: userGroups, excludeGroups },
        applications: { includeAll, includeApps, excludeApps },
        clientApps,
        platforms: { includePlatforms: platforms, excludePlatforms },
        locations: { includeLocations, excludeLocations }
      };
    };
  

    return (
      <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
              borderBottom: '1px solid #c5cae9',
              mb: 0,
              pb: 2,
              pt: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#3f51b5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}
              >
                <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.3rem",
                  fontWeight: 600,
                  color: "#283593",
                  letterSpacing: "0.01em",
                  flexGrow: 1
                }}
              >
                Conditional Access Policies
              </Typography>
              <Chip
                size="small"
                label="Conditional Access"
                sx={{
                  backgroundColor: '#c5cae9',
                  color: '#283593',
                  fontWeight: 500
                }}
              />
            </Box>
          </CardContent>
        <CardContent>          
          {/* Applied CAPs Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "primary.main",
                  letterSpacing: "0.01em",
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <PolicyIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                Policies Applied to {app.displayName}
              </Typography>
            </AccordionSummary>
            
            <AccordionDetails sx={{ backgroundColor: "#fafafa", borderRadius: 1, p: 2 }}>
              {/* Loading state with skeleton */}
              {capLoading && (
                <Box sx={{ p: 1 }}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                </Box>
              )}
              
              {/* Error state */}
              {capError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {capError}
                </Alert>
              )}
              
              {/* Empty state */}
              {!capLoading && !capError && caps.length === 0 && (
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  border: '1px dashed #ccc'
                }}>
                  <SecurityIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No conditional access policies apply to this application.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This means the application may be accessible without additional security controls.
                  </Typography>
                </Box>
              )}
              
              {/* List of policies */}
              {!capLoading && !capError && caps.length > 0 && (
                <List sx={{ p: 0 }}>
                  {caps.map((policy, index) => {
                    const formattedConditions = getFormattedConditions(policy);
                    
                    return (
                      <Paper 
                        key={policy.id} 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          mb: index < caps.length - 1 ? 2 : 0,
                          border: '1px solid #e0e0e0',
                          borderLeft: `4px solid ${policy.state === 'enabled' ? '#2e7d32' : 
                                                 policy.state === 'enabledForReportingButNotEnforced' ? '#ed6c02' : 
                                                 '#9e9e9e'}`,
                          borderRadius: 1,
                          backgroundColor: '#ffffff'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                            {policy.displayName}
                          </Typography>
                          
                          <Stack direction="row" spacing={1}>
                            {getPolicyStateChip(policy.state)}
                            <Chip 
                              size="small" 
                              label={getPolicySeverity(policy).label}
                              color={getPolicySeverity(policy).color as any}
                              icon={getPolicySeverity(policy).level === 'critical' ? <BlockIcon /> : 
                                    getPolicySeverity(policy).level === 'high' ? <ShieldIcon /> : 
                                    <SecurityIcon />}
                              sx={{ fontWeight: 500 }}
                            />
                          </Stack>
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        {/* Grant Controls - What users must do to access */}
                        {policy.grantControls && (
                          <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(63, 81, 181, 0.04)', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <SecurityIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                Access Requirements:
                              </Typography>
                            </Box>
                            
                            {policy.grantControls.builtInControls && policy.grantControls.builtInControls.length > 0 ? (
                              <>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                  {policy.grantControls.builtInControls.map((control: string) => (
                                    <Chip
                                      key={control}
                                      size="small"
                                      icon={getGrantControlIcon(control)}
                                      label={formatGrantControl(control)}
                                      color={control === 'block' ? 'error' : 'primary'}
                                      variant={control === 'block' ? 'filled' : 'outlined'}
                                      sx={{ fontWeight: 500 }}
                                    />
                                  ))}
                                </Box>
                                
                                {policy.grantControls.operator && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {policy.grantControls.operator === 'AND' 
                                      ? '⚠️ All controls above must be satisfied' 
                                      : 'ℹ️ Any one of the controls above must be satisfied'}
                                  </Typography>
                                )}
                                
                                {policy.grantControls.authenticationStrength && (
                                  <Box sx={{ mt: 1, ml: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      <strong>Authentication Strength:</strong> {policy.grantControls.authenticationStrength.displayName || 'Custom strength policy applied'}
                                    </Typography>
                                  </Box>
                                )}
                                
                                {policy.grantControls.termsOfUse && policy.grantControls.termsOfUse.length > 0 && (
                                  <Box sx={{ mt: 1, ml: 1 }}>
                                    <Chip 
                                      size="small" 
                                      icon={<DescriptionIcon fontSize="small" />}
                                      label={`Terms of Use Required (${policy.grantControls.termsOfUse.length})`}
                                      variant="outlined"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </Box>
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                No specific grant controls configured
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* Session Controls - How sessions are managed */}
                        {policy.sessionControls && (
                          <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(237, 108, 2, 0.04)', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <TimerIcon sx={{ mr: 1, color: 'warning.main', fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                Session Controls:
                              </Typography>
                            </Box>
                            
                            <Stack spacing={0.75} sx={{ ml: 1 }}>
                              {policy.sessionControls.signInFrequency?.isEnabled && (
                                <Typography variant="body2" color="text.secondary">
                                  <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Re-authentication required:</strong> Every {policy.sessionControls.signInFrequency.value}{' '}
                                  {policy.sessionControls.signInFrequency.type === 'hours' ? 'hour(s)' : 'day(s)'}
                                  {policy.sessionControls.signInFrequency.authenticationType && 
                                    ` (${policy.sessionControls.signInFrequency.authenticationType})`}
                                </Typography>
                              )}
                              
                              {policy.sessionControls.persistentBrowser?.isEnabled && (
                                <Typography variant="body2" color="text.secondary">
                                  <DevicesIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Browser session:</strong> {policy.sessionControls.persistentBrowser.mode === 'always' 
                                    ? 'Always persistent (stay signed in)' 
                                    : 'Never persistent (sign in each time)'}
                                </Typography>
                              )}
                              
                              {policy.sessionControls.cloudAppSecurity?.isEnabled && (
                                <Typography variant="body2" color="text.secondary">
                                  <ShieldIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Cloud App Security:</strong> {policy.sessionControls.cloudAppSecurity.cloudAppSecurityType || 'Monitoring enabled'}
                                </Typography>
                              )}
                              
                              {policy.sessionControls.applicationEnforcedRestrictions?.isEnabled && (
                                <Typography variant="body2" color="text.secondary">
                                  <LockIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>App restrictions:</strong> Application-enforced limitations apply
                                </Typography>
                              )}
                              
                              {policy.sessionControls.disableResilienceDefaults && (
                                <Typography variant="body2" color="warning.main">
                                  <WarningAmberIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Resilience disabled:</strong> No fallback access during outages
                                </Typography>
                              )}
                              
                              {!policy.sessionControls.signInFrequency?.isEnabled && 
                               !policy.sessionControls.persistentBrowser?.isEnabled &&
                               !policy.sessionControls.cloudAppSecurity?.isEnabled &&
                               !policy.sessionControls.applicationEnforcedRestrictions?.isEnabled &&
                               !policy.sessionControls.disableResilienceDefaults && (
                                <Typography variant="body2" color="text.secondary">
                                  No specific session controls configured
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                        
                        {/* Risk-Based Conditions */}
                        {((policy.conditions.signInRiskLevels && policy.conditions.signInRiskLevels.length > 0) ||
                          (policy.conditions.userRiskLevels && policy.conditions.userRiskLevels.length > 0) ||
                          (policy.conditions.servicePrincipalRiskLevels && policy.conditions.servicePrincipalRiskLevels.length > 0)) && (
                          <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.04)', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <WarningAmberIcon sx={{ mr: 1, color: 'error.main', fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                Risk-Based Enforcement:
                              </Typography>
                            </Box>
                            
                            <Stack spacing={0.75} sx={{ ml: 1 }}>
                              {policy.conditions.signInRiskLevels && policy.conditions.signInRiskLevels.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  <ShieldIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Sign-in risk:</strong> Triggered at {policy.conditions.signInRiskLevels.join(', ')} risk level(s)
                                </Typography>
                              )}
                              
                              {policy.conditions.userRiskLevels && policy.conditions.userRiskLevels.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  <GroupIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>User risk:</strong> Triggered at {policy.conditions.userRiskLevels.join(', ')} risk level(s)
                                </Typography>
                              )}
                              
                              {policy.conditions.servicePrincipalRiskLevels && policy.conditions.servicePrincipalRiskLevels.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  <SecurityIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                  <strong>Service Principal risk:</strong> Triggered at {policy.conditions.servicePrincipalRiskLevels.join(', ')} risk level(s)
                                </Typography>
                              )}
                            </Stack>
                            
                            <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                              <Typography variant="caption">
                                This policy uses Azure AD Identity Protection to dynamically adjust requirements based on detected risk.
                              </Typography>
                            </Alert>
                          </Box>
                        )}
                        
                        <Stack spacing={1.5}>
                          {/* User Conditions */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <GroupIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                User Assignment:
                              </Typography>
                              <Box sx={{ ml: 1 }}>
                                {formattedConditions.users.includeGroups.length > 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Included: {formattedConditions.users.includeGroups.join(", ")}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Applied to: All users
                                  </Typography>
                                )}
                                
                                {formattedConditions.users.excludeGroups.length > 0 && (
                                  <Typography variant="body2" color="text.secondary">
                                    Excluded: {formattedConditions.users.excludeGroups.join(", ")}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* App Conditions */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <LockIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Application Access:
                              </Typography>
                              <Box sx={{ ml: 1 }}>
                                {formattedConditions.applications.includeAll ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Applied to: All cloud apps
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Included apps: {formattedConditions.applications.includeApps.join(", ")}
                                  </Typography>
                                )}
                                
                                {formattedConditions.applications.excludeApps.length > 0 && (
                                  <Typography variant="body2" color="text.secondary">
                                    Excluded apps: {formattedConditions.applications.excludeApps.join(", ")}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Client app types */}
                          {formattedConditions.clientApps.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <DevicesIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary', fontSize: 20 }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  Client App Types:
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                  {formattedConditions.clientApps.join(", ")}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
          
          {/* ABOUT CAP Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography 
                variant="subtitle1"
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.1rem", color: 'info.main' }} />
                About Conditional Access
              </Typography>
            </AccordionSummary>
  
            <AccordionDetails>
              <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Conditional Access Policies (CAPs) are a set of rules that control when and how users can access applications in your organization.
                </Typography>
                
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e0e0ff', 
                  borderRadius: 1,
                  mb: 2
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                    How Conditional Access Works:
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Signal-based access</strong>: Policies examine signals like user, device, location, and risk<br />
                    • <strong>Decisions</strong>: Based on signals, access is granted, denied, or additional verification is required<br />
                    • <strong>Enforcement</strong>: Policies may require MFA, compliant devices, or specific locations<br />
                    • <strong>Report-only mode</strong>: Policies can be tested without impacting users
                  </Typography>
                </Box>
                
                <Box sx={{
                  p: 2,
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffe082',
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                    Common CAP Requirements:
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Multi-factor authentication</strong>: Require MFA for sensitive apps or risky sign-ins<br />
                    • <strong>Device management</strong>: Restrict access to managed or compliant devices<br />
                    • <strong>Location-based access</strong>: Limit access from specific countries or networks<br />
                    • <strong>Risk-based controls</strong>: Adapt requirements based on sign-in or user risk
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  };

export default CapsApplied;
